import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/ui/Button"
import {
  Settings,
  Sheet,
  ChartLine,
  BetweenHorizontalStart,
  WandSparkles,
  PieChart,
  Globe,
  LayoutGrid,
  Map,
  Building2,
  TrendingUp,
} from "lucide-react"

export type MenuAction = {
  type: "action"
  label: string
  toolName: string
  data?: any
  visible?: boolean
  icon?: React.ReactNode
  buttonProps?: Partial<React.ComponentProps<typeof Button>>
}

export type MenuSubmenu = {
  type: "submenu"
  label: string
  children: MenuNode[]
  visible?: boolean
  icon?: React.ReactNode
  buttonProps?: Partial<React.ComponentProps<typeof Button>>
}

export type MenuNode = MenuAction | MenuSubmenu

const RowIcon = ({ node, size = 18 }: { node: MenuNode; size?: number }) => {
  if (node.type === "submenu") {
    return <BetweenHorizontalStart size={size} />
  }

  if (node.icon === "sheet") {
    return <Sheet size={size} />
  } else if (node.icon === "chartLine") {
    return <ChartLine size={size} />
  } else if (node.icon === "wandSparkles") {
    return <WandSparkles size={size} />
  } else if (node.icon === "pieChart") {
    return <PieChart size={size} />
  } else if (node.icon === "globe") {
    return <Globe size={size} />
  } else if (node.icon === "grid") {
    return <LayoutGrid size={size} />
  } else if (node.icon === "map") {
    return <Map size={size} />
  } else if (node.icon === "building") {
    return <Building2 size={size} />
  } else if (node.icon === "trending") {
    return <TrendingUp size={size} />
  } else {
    return <Settings size={size} />
  }
}

export const EditorJsMenu = ({
  root,
  onSelect,
  onBackLabel = "Back",
}: {
  root: MenuNode[]
  onSelect: (toolName: string, data?: any) => void
  onBackLabel?: string
}) => {
  const [stack, setStack] = useState<MenuNode[][]>([root])
  const level = stack[stack.length - 1]!
  const items = useMemo(() => level.filter((n) => n.visible !== false), [level])

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    containerRef.current?.focus()
  }, [stack.length])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && stack.length > 1) {
      e.preventDefault()
      setStack((s) => s.slice(0, -1))
    }
  }

  const RowButton = ({
    node,
    onClick,
  }: {
    node: MenuNode
    onClick: () => void
  }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      icon={<RowIcon node={node} />}
      style={{ justifyContent: "left" }}
      className="ej-menu-row"
      {...node.buttonProps}
    >
      {node.label}
      {node.type === "submenu" && <span>&nbsp;{`->`}</span>}
    </Button>
  )

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="ej-menu editorjs-custom-menu-container"
    >
      {stack.length > 1 && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStack((s) => s.slice(0, -1))}
          >
            <span>
              <span>{`<-`}&nbsp;</span>
              <span>{onBackLabel}</span>
            </span>
          </Button>
        </div>
      )}

      {items.map((node, i) =>
        node.type === "action" ? (
          <RowButton
            key={`a-${i}-${node.label}`}
            node={node}
            onClick={() => onSelect(node.toolName, node.data)}
          />
        ) : (
          <RowButton
            key={`s-${i}-${node.label}`}
            node={node}
            onClick={() => setStack((s) => [...s, node.children])}
          />
        ),
      )}
    </div>
  )
}
